import React, { useState, useEffect } from 'react';
import {
  Table, Button, Tag, Input, Modal, message, Typography, Empty, Result,
  Descriptions, Space, Tooltip, Card, Row, Col, Select, Spin, Divider, Segmented, Badge,
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, FileTextOutlined, FileTextFilled,
  WarningOutlined, SaveOutlined, LinkOutlined, CheckCircleFilled,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  getFarmaceuticos,
  updateFarmaceuticoEstado,
  getAuraquanticCliente,
  vincularFarmaceutico,
  ESTADO_PENDIENTE,
  ESTADO_REALIZADO,
  ESTADO_DESCARTADO,
  type ReconciliacionFarmaceutico,
  type AuraquanticClienteData,
} from '../../api/reconciliacionApi';

const { Text } = Typography;
const { TextArea } = Input;

const CRM_CLIENT_URL = 'https://gedeco.farmaconsulting.es/FamiliasDefinibles_Ficha.aspx?id=';
const CRM_CLIENT_URL_PARAMS = '&idFam=25&idFamCls=59&pag=242&sraccess=1&tmp=758';

const estadoLabels: Record<number, { label: string; color: string }> = {
  0: { label: 'Pendiente', color: 'orange' },
  1: { label: 'Realizado', color: 'green' },
  2: { label: 'Descartado', color: 'default' },
};

interface FarmaceuticosTabProps {
  selectedRecIds?: number[];
}

const FarmaceuticosTab: React.FC<FarmaceuticosTabProps> = ({ selectedRecIds }) => {
  const ESTADO_TODOS = -1;
  const [loading, setLoading] = useState(true);
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState<ReconciliacionFarmaceutico[]>([]);
  const [filteredData, setFilteredData] = useState<ReconciliacionFarmaceutico[]>([]);
  const [selectedEstado, setSelectedEstado] = useState<number>(ESTADO_PENDIENTE);
  const [estadoCounts, setEstadoCounts] = useState({ pendiente: 0, realizado: 0, descartado: 0 });
  const [searchText, setSearchText] = useState('');

  // Modal detalle
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ReconciliacionFarmaceutico | null>(null);
  const [editNotas, setEditNotas] = useState('');
  const [editEstadoId, setEditEstadoId] = useState<number>(ESTADO_PENDIENTE);
  const [actionLoading, setActionLoading] = useState(false);

  // Auraquantic lookup
  const [auraquanticIdInput, setAuraquanticIdInput] = useState('');
  const [auraquanticData, setAuraquanticData] = useState<AuraquanticClienteData | null>(null);
  const [auraquanticLoading, setAuraquanticLoading] = useState(false);
  const [auraquanticError, setAuraquanticError] = useState<string | null>(null);

  // Vinculación
  const [vinculando, setVinculando] = useState(false);

  const fetchByEstado = async (estadoId: number) => {
    const apiEstado = estadoId === ESTADO_TODOS ? undefined : estadoId;
    if (!selectedRecIds || selectedRecIds.length === 0) return getFarmaceuticos(undefined, apiEstado);
    const results = await Promise.all(selectedRecIds.map(rid => getFarmaceuticos(rid, apiEstado)));
    return results.flat();
  };

  const loadCounts = async () => {
    setLoadingCounts(true);
    try {
      const [pendientes, realizados, descartados] = await Promise.all([
        fetchByEstado(ESTADO_PENDIENTE),
        fetchByEstado(ESTADO_REALIZADO),
        fetchByEstado(ESTADO_DESCARTADO),
      ]);
      setEstadoCounts({ pendiente: pendientes.length, realizado: realizados.length, descartado: descartados.length });
    } catch { /* counts fail silently */ }
    finally { setLoadingCounts(false); }
  };

  const loadData = async () => {
    setLoading(true);
    setError(false);
    try {
      const result = await fetchByEstado(selectedEstado);
      setData(result);
      applySearch(result, searchText);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCounts();
  }, [selectedRecIds]);

  useEffect(() => {
    loadData();
  }, [selectedEstado, selectedRecIds]);

  const applySearch = (items: ReconciliacionFarmaceutico[], text: string) => {
    if (!text.trim()) {
      setFilteredData(items);
      return;
    }
    const lower = text.toLowerCase();
    setFilteredData(items.filter(f =>
      (f.nombre || '').toLowerCase().includes(lower) ||
      (f.numColegiado || '').toLowerCase().includes(lower) ||
      (f.nif || '').toLowerCase().includes(lower) ||
      (f.colegio || '').toLowerCase().includes(lower)
    ));
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    applySearch(data, value);
  };

  const handleUpdateEstado = async (id: number, estadoId: number, notasGestion?: string) => {
    setActionLoading(true);
    try {
      await updateFarmaceuticoEstado(id, estadoId, notasGestion);
      const label = estadoLabels[estadoId]?.label || 'actualizado';
      message.success(`Farmacéutico ${label.toLowerCase()} correctamente`);
      setModalVisible(false);
      loadData();
      loadCounts();
    } catch {
      message.error('Error al actualizar el farmacéutico');
    } finally {
      setActionLoading(false);
    }
  };

  const openDetail = async (item: ReconciliacionFarmaceutico) => {
    setSelectedItem(item);
    setEditNotas(item.notasGestion || '');
    setEditEstadoId(item.estadoId);
    setAuraquanticIdInput('');
    setAuraquanticData(null);
    setAuraquanticError(null);
    setModalVisible(true);

    // Si ya está vinculado, cargar automáticamente los datos de Gescom
    if (item.idColegiadoGS) {
      setAuraquanticLoading(true);
      try {
        const data = await getAuraquanticCliente(item.idColegiadoGS);
        setAuraquanticData(data);
      } catch {
        // No bloquear la apertura si falla la carga
      } finally {
        setAuraquanticLoading(false);
      }
    }
  };

  // Buscar datos de Auraquantic por ID
  const handleBuscarAuraquantic = async () => {
    const id = parseInt(auraquanticIdInput, 10);
    if (isNaN(id) || id <= 0) {
      setAuraquanticError('Introduce un ID numérico válido');
      return;
    }
    setAuraquanticLoading(true);
    setAuraquanticError(null);
    setAuraquanticData(null);
    try {
      const data = await getAuraquanticCliente(id);
      setAuraquanticData(data);
    } catch {
      setAuraquanticError('No se encontró el cliente en Auraquantic o error de conexión');
    } finally {
      setAuraquanticLoading(false);
    }
  };

  // Vincular registros
  const handleVincular = async () => {
    if (!selectedItem || !auraquanticData) return;
    setVinculando(true);
    try {
      const result = await vincularFarmaceutico(selectedItem.id, auraquanticData.id);
      const code = parseInt(result.code, 10);

      if (code < 0) {
        message.error(result.message);
        return;
      }

      // Éxito: recargar datos del cliente Gescom para verificar vinculación
      try {
        const refreshed = await getAuraquanticCliente(auraquanticData.id);
        // Verificar que id_colegiado_CU2 de Gescom coincide con id_colegiado de CU2
        if (refreshed.idColegiadoCU2 !== selectedItem.idColegiadoCU2) {
          message.error(`Verificación fallida: id_colegiado_CU2 en Gescom (${refreshed.idColegiadoCU2}) no coincide con id_colegiado de CU2 (${selectedItem.idColegiadoCU2}). La vinculación puede no haberse realizado correctamente.`);
          setAuraquanticData(refreshed);
          return;
        }
        setAuraquanticData(refreshed);
      } catch {
        // Si falla la recarga, no bloqueamos
      }

      // Actualizar el item local para reflejar la vinculación sin cerrar el modal
      const updated = { ...selectedItem, idColegiadoGS: auraquanticData.id };
      setSelectedItem(updated);
      setData(prev => prev.map(f => f.id === updated.id ? updated : f));
      setFilteredData(prev => prev.map(f => f.id === updated.id ? updated : f));
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Error al vincular los registros';
      message.error(msg);
    } finally {
      setVinculando(false);
    }
  };

  const handleEstadoChange = (value: string | number) => {
    setSelectedEstado(Number(value));
  };

  const columns: ColumnsType<ReconciliacionFarmaceutico> = [
    {
      title: 'Rec',
      dataIndex: 'reconciliacionId',
      key: 'rec',
      width: 45,
      render: (val: number) => <Text type="secondary" style={{ fontSize: 12 }}>{val}</Text>,
    },
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
      ellipsis: true,
      width: 220,
    },
    {
      title: 'id_colegiado',
      dataIndex: 'idColegiadoCU2',
      key: 'idColegiado',
      width: 100,
      render: (val: number) => <Text code>{val}</Text>,
    },
    {
      title: 'id_colegiado_gs',
      dataIndex: 'idColegiadoGS',
      key: 'idColegiadoGS',
      width: 110,
      render: (val?: number) => val ? <Text code>{val}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Nº Colegiado',
      dataIndex: 'numColegiado',
      key: 'numColegiado',
      width: 110,
    },
    {
      title: 'Colegio',
      dataIndex: 'colegio',
      key: 'colegio',
      width: 140,
      ellipsis: true,
    },
    {
      title: 'NIF',
      dataIndex: 'nif',
      key: 'nif',
      width: 100,
    },
    {
      title: 'Rol',
      dataIndex: 'rol',
      key: 'rol',
      width: 90,
      filters: [
        { text: 'Titular', value: 'titular' },
        { text: 'Empleado', value: 'empleado' },
      ],
      onFilter: (value, record) => (record.rol || '').toLowerCase() === (value as string).toLowerCase(),
      render: (val?: string) => val ? <Tag color={val.toLowerCase() === 'titular' ? 'blue' : 'cyan'}>{val}</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Rel.',
      key: 'relaciones',
      width: 60,
      render: (_, r) => r.relacionesPendientes ? (
        <Tooltip title="Tiene titularidades/empleados pendientes"><Tag color="volcano" icon={<WarningOutlined />}>Sí</Tag></Tooltip>
      ) : <Text type="secondary">—</Text>,
    },
    {
      title: 'Fecha',
      dataIndex: 'fechaCreacion',
      key: 'fecha',
      width: 100,
      render: (val: string) => new Date(val).toLocaleDateString('es-ES'),
    },
    {
      title: 'Estado',
      dataIndex: 'estadoId',
      key: 'estado',
      width: 100,
      render: (val: number) => {
        const est = estadoLabels[val] || { label: `Estado ${val}`, color: 'default' };
        return <Tag color={est.color}>{est.label}</Tag>;
      },
    },
    {
      title: '',
      key: 'notas',
      width: 40,
      render: (_, r) => r.notasGestion ? (
        <Tooltip title={r.notasGestion}><FileTextFilled style={{ color: '#1890ff' }} /></Tooltip>
      ) : (
        <FileTextOutlined style={{ color: '#d9d9d9' }} />
      ),
    },
  ];

  if (error) {
    return (
      <Result
        status="error"
        title="Error al cargar farmacéuticos"
        extra={<Button type="primary" onClick={loadData} icon={<ReloadOutlined />}>Reintentar</Button>}
      />
    );
  }

  // ¿Ya está vinculado este item?
  const yaVinculado = selectedItem ? !!selectedItem.idColegiadoGS : false;

  // Coincidencias verificadas tras vinculación
  const matchIdColegiado = yaVinculado && auraquanticData?.idColegiadoCU2 === selectedItem?.idColegiadoCU2;
  const matchIdGS = yaVinculado && selectedItem?.idColegiadoGS === auraquanticData?.id;
  const greenCodeStyle: React.CSSProperties = { backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 4, padding: '0 4px' };

  return (
    <div>
      {/* Cabecera */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Input
          placeholder="Buscar por nombre, colegiado, NIF..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={e => handleSearch(e.target.value)}
          style={{ width: 280 }}
          allowClear
        />
        <Segmented
          value={selectedEstado}
          onChange={handleEstadoChange}
          disabled={loading || loadingCounts}
          options={[
            {
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>Todos</span>
                  <Badge
                    count={estadoCounts.pendiente + estadoCounts.realizado + estadoCounts.descartado}
                    style={{ backgroundColor: '#595959' }}
                    showZero
                    overflowCount={9999}
                  />
                </div>
              ),
              value: ESTADO_TODOS,
            },
            {
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>Pendiente</span>
                  <Badge
                    count={estadoCounts.pendiente}
                    style={{ backgroundColor: '#fa8c16' }}
                    showZero
                    overflowCount={9999}
                  />
                </div>
              ),
              value: ESTADO_PENDIENTE,
            },
            {
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>Realizado</span>
                  <Badge
                    count={estadoCounts.realizado}
                    style={{ backgroundColor: '#52c41a' }}
                    showZero
                    overflowCount={9999}
                  />
                </div>
              ),
              value: ESTADO_REALIZADO,
            },
            {
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>Descartado</span>
                  <Badge
                    count={estadoCounts.descartado}
                    style={{ backgroundColor: '#8c8c8c' }}
                    showZero
                    overflowCount={9999}
                  />
                </div>
              ),
              value: ESTADO_DESCARTADO,
            },
          ]}
          size="large"
        />
        <Button icon={<ReloadOutlined />} onClick={() => { loadData(); loadCounts(); }}>Refrescar</Button>
      </div>

      {/* Tabla */}
      <Table
        dataSource={filteredData.map(f => ({ ...f, key: f.id }))}
        columns={columns}
        loading={loading}
        pagination={{ defaultPageSize: 100, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'], showTotal: total => `${total} registros` }}
        onRow={record => ({
          onClick: () => openDetail(record),
          style: { cursor: 'pointer' },
        })}
        size="middle"
        scroll={{ x: 1100 }}
        locale={{ emptyText: <Empty description="No hay farmacéuticos pendientes de conciliación" /> }}
      />

      {/* Modal de detalle — gestión de vinculación */}
      <Modal
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        title={selectedItem ? `Gestión de farmacéutico — ${selectedItem.nombre || 'ID CU2 ' + selectedItem.idColegiadoCU2}` : ''}
        width={900}
        destroyOnClose
      >
        {selectedItem && (
          <div>
            {/* Relaciones pendientes — aviso superior */}
            {selectedItem.relacionesPendientes && (
              <div style={{ background: '#fff2e8', border: '1px solid #ffbb96', borderRadius: 6, padding: 12, marginBottom: 16 }}>
                <WarningOutlined style={{ color: '#d4380d', marginRight: 8 }} />
                <Text strong style={{ color: '#d4380d' }}>Titularidades / empleados pendientes de este farmacéutico</Text>
                <div style={{ marginTop: 4 }}>
                  <Text style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{selectedItem.relacionesPendientes}</Text>
                </div>
              </div>
            )}

            {/* Dos columnas: CU2 (izq) | Auraquantic (der) */}
            <Row gutter={16}>
              {/* ─── Columna CU2 ─── */}
              <Col xs={24} md={12}>
                <Card
                  title={<span style={{ color: '#1890ff' }}>Datos CU2</span>}
                  size="small"
                  style={{ marginBottom: 16 }}
                  headStyle={{ background: '#e6f7ff', borderBottom: '1px solid #91d5ff' }}
                >
                  <Descriptions size="small" column={1} colon={false} labelStyle={{ fontWeight: 500, width: 120, color: '#595959' }}>
                    <Descriptions.Item label="Nombre"><Text strong>{selectedItem.nombre || '—'}</Text></Descriptions.Item>
                    <Descriptions.Item label="id_colegiado">
                      <span style={matchIdColegiado ? greenCodeStyle : undefined}><Text code>{selectedItem.idColegiadoCU2}</Text></span>
                    </Descriptions.Item>
                    <Descriptions.Item label="id_colegiado_gs">
                      {selectedItem.idColegiadoGS
                        ? <span style={matchIdGS ? greenCodeStyle : undefined}><Text code>{selectedItem.idColegiadoGS}</Text></span>
                        : <Text type="secondary">sin vincular</Text>
                      }
                    </Descriptions.Item>
                    <Descriptions.Item label="Nº Colegiado">{selectedItem.numColegiado || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Colegio">{selectedItem.colegio || '—'}</Descriptions.Item>
                    <Descriptions.Item label="NIF">{selectedItem.nif || '—'}</Descriptions.Item>
                    <Descriptions.Item label="CP">{selectedItem.cp || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Email">{selectedItem.email || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Rol">{selectedItem.rol || '—'}</Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>

              {/* ─── Columna Gescom ─── */}
              <Col xs={24} md={12}>
                <Card
                  title={<span style={{ color: '#389e0d' }}>Datos Gescom</span>}
                  size="small"
                  style={{ marginBottom: 16, borderColor: '#b7eb8f' }}
                  headStyle={{ backgroundColor: '#f6ffed', borderBottom: '1px solid #b7eb8f', fontWeight: 'bold', color: '#389e0d' }}
                >
                  <Spin spinning={auraquanticLoading}>
                    <Descriptions size="small" column={1} colon={false} labelStyle={{ fontWeight: 500, width: 120, color: '#595959' }}>
                      <Descriptions.Item label="Nombre">
                        {auraquanticData ? (
                          <a href={`${CRM_CLIENT_URL}${auraquanticData.id}${CRM_CLIENT_URL_PARAMS}`} target="_blank" rel="noopener noreferrer">
                            <Text strong>{auraquanticData.nombre}</Text>
                          </a>
                        ) : <Text strong>—</Text>}
                      </Descriptions.Item>
                      <Descriptions.Item label="id_colegiado_CU2">
                        {auraquanticData?.idColegiadoCU2
                          ? <span style={matchIdColegiado ? greenCodeStyle : undefined}><Text code>{auraquanticData.idColegiadoCU2}</Text></span>
                          : <Text type="secondary">{auraquanticData ? 'sin asignar' : '—'}</Text>
                        }
                      </Descriptions.Item>
                      <Descriptions.Item label="ID">
                        {yaVinculado ? (
                          <span style={matchIdGS ? greenCodeStyle : undefined}><Text code>{selectedItem.idColegiadoGS}</Text></span>
                        ) : (
                          <>
                            <Space.Compact size="small">
                              <Input
                                placeholder="ID"
                                value={auraquanticIdInput}
                                onChange={e => setAuraquanticIdInput(e.target.value)}
                                onPressEnter={handleBuscarAuraquantic}
                                style={{ width: 100 }}
                              />
                              <Button
                                icon={<ReloadOutlined />}
                                onClick={handleBuscarAuraquantic}
                                loading={auraquanticLoading}
                                size="small"
                              />
                            </Space.Compact>
                            {auraquanticError && (
                              <Text type="danger" style={{ fontSize: 12, marginLeft: 8 }}>{auraquanticError}</Text>
                            )}
                          </>
                        )}
                      </Descriptions.Item>
                      <Descriptions.Item label="Nº Colegiado">{auraquanticData?.numColegiado || <Text type="secondary">—</Text>}</Descriptions.Item>
                      <Descriptions.Item label="Provincia">{auraquanticData?.provincia || <Text type="secondary">—</Text>}</Descriptions.Item>
                      <Descriptions.Item label="NIF">{auraquanticData?.nif || <Text type="secondary">—</Text>}</Descriptions.Item>
                      <Descriptions.Item label="CP">{auraquanticData?.cp || <Text type="secondary">—</Text>}</Descriptions.Item>
                      <Descriptions.Item label="Email">{auraquanticData?.email || <Text type="secondary">—</Text>}</Descriptions.Item>
                    </Descriptions>
                  </Spin>
                </Card>
              </Col>
            </Row>

            {/* Zona de vinculación */}
            {yaVinculado ? (
              <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, padding: 12, marginBottom: 16, textAlign: 'center' }}>
                <CheckCircleFilled style={{ color: '#52c41a', fontSize: 16, marginRight: 8 }} />
                <Text strong style={{ color: '#389e0d' }}>Registros vinculados</Text>
              </div>
            ) : auraquanticData && !auraquanticData.idColegiadoCU2 && selectedItem.estadoId === ESTADO_PENDIENTE ? (
              <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, padding: 16, marginBottom: 16, textAlign: 'center' }}>
                <Button
                  type="primary"
                  size="large"
                  icon={<LinkOutlined />}
                  loading={vinculando}
                  onClick={() => {
                    Modal.confirm({
                      width: 520,
                      title: 'Vincular registros',
                      content: (
                        <div>
                          <p>Se vinculará el farmacéutico de CU2 con el registro de Gescom:</p>
                          <ul style={{ margin: '8px 0' }}>
                            <li><strong>CU2:</strong> {selectedItem.nombre} (id_colegiado: {selectedItem.idColegiadoCU2})</li>
                            <li><strong>Gescom:</strong> {auraquanticData.nombre} (ID: {auraquanticData.id})</li>
                          </ul>
                          <p>La vinculación se registrará en ambas bases de datos. ¿Continuar?</p>
                        </div>
                      ),
                      okText: 'Vincular',
                      cancelText: 'Cancelar',
                      onOk: handleVincular,
                    });
                  }}
                >
                  Vincular registros
                </Button>
              </div>
            ) : null}

            <Divider style={{ margin: '12px 0' }} />

            {/* Comentario */}
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Comentario</Text>
            <TextArea
              value={editNotas}
              onChange={e => setEditNotas(e.target.value)}
              autoSize={{ minRows: 2, maxRows: 5 }}
              placeholder="Añadir comentario..."
              style={{ marginBottom: 16 }}
            />

            {/* Acciones: estado selector + Guardar */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <Select
                value={editEstadoId}
                onChange={setEditEstadoId}
                style={{ width: 160 }}
              >
                <Select.Option value={ESTADO_PENDIENTE}><span style={{ color: '#fa8c16' }}>● Pendiente</span></Select.Option>
                <Select.Option value={ESTADO_REALIZADO}><span style={{ color: '#52c41a' }}>● Realizado</span></Select.Option>
                <Select.Option value={ESTADO_DESCARTADO}><span style={{ color: '#8c8c8c' }}>● Descartado</span></Select.Option>
              </Select>

              <Button
                icon={<SaveOutlined />}
                onClick={() => handleUpdateEstado(selectedItem.id, editEstadoId, editNotas || undefined)}
                loading={actionLoading}
              >
                Guardar
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default FarmaceuticosTab;
